import styles from "./bond.module.css"
import React from "react"
import * as finance from "shared/finance"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import {DoubleBar} from "story/components/graphs/bar"
import * as graphUtil from "shared/graphs/util"
import AccessibilityTable from "activities/components/shared/accessibility-table"

// a = p * Math.pow(1 + r/2, 2t)
// so principal is the faceValue
// then the rate is the coupon rate

export const amountWithInterest = (faceValue, months, rate) => {
  // months is months elapsed since purchase
  const years = months / 12
  return rate ? faceValue * Math.pow(1 + rate / 2, years * 2) : faceValue
}

const getChartData = (faceValue, months, rate) =>
  util.xAxisArray(months).reduce((memo, month) => [...memo, [month, faceValue, util.round(amountWithInterest(faceValue, month, rate) - faceValue)]], [])

const calculate = ({price, faceValue, months, rate}) => {
  const data = getChartData(faceValue, months, rate)
  const totalReturn = amountWithInterest(faceValue, months, rate)

  return {
    chartData: getChartData(faceValue, months, rate),
    annualInterest: rate * faceValue,
    totalInterest: totalReturn - faceValue,
    totalReturn,
    totalEarned: totalReturn - price
  }
}

const accessibilityTableLabel = "Bond Earnings Summary"
const accessibilityTableHeadings = [
  {name: "Month", key: "month"},
  {name: "Face Value", key: "faceValue"},
  {name: "Interest Earned", key: "interest"}
]
const accessibilityTableData = (data) =>
  data.map((row) => ({
    month: row[0],
    faceValue: util.formatDollarsNoCents(row[1]),
    interest: util.formatDollarsNoCents(row[2])
  }))

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {price, faceValue, years, rate, resetKey} = getState()
    const {id, theme} = getProps()
    const {chartData, annualInterest, totalInterest, totalReturn, totalEarned} = calculate({price, faceValue, months: years * 12, rate: rate ? rate / 100 : 0})

    return (
      <div>
        <shared.Content className={styles["calc-container"]}>
          <shared.ChartContainer className={styles["chart-container"]}>
            <DoubleBar
              isStacked={true}
              values={chartData}
              yAxis="Total Value"
              xAxis="Months"
              yAxisWidth={40}
              formatter="currency"
              tooltip={[
                "Month",
                {name: "Face Value", type: "currency", color: graphUtil.getColor(theme, 2)},
                {name: "Interest Earned", type: "currency", color: graphUtil.getColor(theme, 4)}
              ]}
              customTooltipStyles={{width: 281}}
              type="calculator"
              colors={[graphUtil.getColor(theme, 2), graphUtil.getColor(theme, 1), graphUtil.getColor(theme, 4)]}
              ariaLabel={`This bar graph visually represents how much you interest you can earn over time on a bond.`}
            />
          </shared.ChartContainer>
          <shared.SummaryContainer className={styles["summary-container"]}>
            <callouts.Callout calloutStyles={styles["callout-grid"]}>
              <callouts.Detail
                label="Annual Interest"
                result={util.formatDollarsNoCents(annualInterest)}
                dataTestId="annual-interest"
                statStyles={styles["stat"]}
                resultStyles={styles["result"]}
                labelStyles={styles["stat-label"]}
                className="test"
              />
              <callouts.Detail
                label="Total Interest"
                result={util.formatDollarsNoCents(totalInterest)}
                dataTestId="total-interest"
                statStyles={styles["stat"]}
                resultStyles={styles["result"]}
                labelStyles={styles["stat-label"]}
                className="test"
              />
              <callouts.Detail
                label="Total Return"
                result={util.formatDollarsNoCents(totalReturn)}
                dataTestId="total-return"
                statStyles={styles["stat"]}
                resultStyles={styles["result"]}
                labelStyles={styles["stat-label"]}
                className="test"
              />
              <callouts.Detail
                label="Total Earned"
                result={util.formatDollarsNoCents(totalEarned)}
                dataTestId="total-earned"
                statStyles={styles["stat"]}
                resultStyles={styles["result"]}
                labelStyles={styles["stat-label"]}
                className="test"
              />
            </callouts.Callout>
          </shared.SummaryContainer>
          <shared.InputsContainer className={styles["inputs-container"]}>
            <inputs.CalcInput
              id={id}
              title="Bond Face Value (Most often $1000)"
              onChange={updateState("faceValue", util.fromString)}
              initialValue={faceValue}
              resetKey={resetKey}
            />
            <inputs.Input id={id} title="Bond Price*" onChange={updateState("price", parseFloat)} value={price} />
            <inputs.Input id={id} title="Coupon Interest Rate" onChange={updateState("rate", parseFloat)} value={rate || 0} valueType="percent" />
            <inputs.RangeInput
              id={id}
              title="Years Until Maturity"
              onChange={updateState("years", util.fromString)}
              initialValue={years}
              min={1}
              max={10}
              step={1}
              units="years"
              valueType="number"
              resetKey={resetKey}
            />
          </shared.InputsContainer>
          <div className={styles["disclaimer"]}>
            *The price will vary if you are buying it on a secondary market, but it will match the face value if you are buying it from a primary market.
          </div>

          <AccessibilityTable label={accessibilityTableLabel} headings={accessibilityTableHeadings} data={accessibilityTableData(chartData)} />
        </shared.Content>
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {faceValue, price, years, rate} = state
      const {annualInterest, totalinterest, totalReturn, totalEarned} = calculate({price, faceValue, months: years * 12, rate: rate / 100})

      return Object.assign(state, {
        annualInterest: util.round(annualInterest),
        totalInterest: util.round(totalInterest),
        totalReturn: util.round(totalReturn),
        totalEarned: util.round(totalEarned)
      })
    },
    defaultProps: {
      id: "bond-calculator",
      className: "bond-calculator",
      title: "Bond Calculator",
      subtitle: "Calculate how much you could earn from a bond."
    },
    defaultState: {
      faceValue: 1000,
      price: 400,
      years: 3,
      rate: 2.5
    }
  }
)
